import { apiUrl } from "../../ApiManager/AxiosHeaders";
import { isEmptyEmailHtml } from "../../GenericComponents/EmailRichTextEditor";

function extractErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    fallback ||
    "Request failed."
  );
}

function assertSuccess(response, fallback) {
  if (response?.data?.success || response?.data?.status) {
    return response.data;
  }
  throw new Error(response?.data?.message || fallback);
}

export async function deleteInquiryBatch(batchId) {
  if (!batchId) {
    throw new Error("Batch id is required.");
  }

  try {
    const response = await apiUrl.delete(
      `/commission-inquiry-batches/${batchId}`,
    );

    const data = assertSuccess(response, "Failed to delete inquiry batch.");

    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to delete inquiry batch."),
    );
  }
}

export async function deleteInquiryBatchItem(batchId, itemId) {
  if (!batchId) {
    throw new Error("Batch id is required.");
  }

  if (!itemId) {
    throw new Error("Batch item id is required.");
  }

  try {
    const response = await apiUrl.delete(
      `/commission-inquiry-batches/${batchId}/items/${itemId}`,
    );

    const data = assertSuccess(
      response,
      "Failed to remove student from batch.",
    );

    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to remove student from batch."),
    );
  }
}

/** GET /commission-inquiry-batches */
export async function fetchInquiryBatches(params = {}) {
  try {
    const response = await apiUrl.get("/commission-inquiry-batches", {
      params: {
        page: params.page || 1,
        perPage: params.perPage || 25,
        status: params.status || undefined,
        application_type: params.applicationType || undefined,
        search: params.search || undefined,
        period_status: params.periodStatus || undefined,
      },
    });
    const data = assertSuccess(response, "Failed to fetch inquiry batches.");
    return {
      batches: Array.isArray(data.data) ? data.data : [],
      openPeriod: data.open_period || null,
      pagination: data.pagination || {
        currentPage: 1,
        perPage: params.perPage || 25,
        total: 0,
        lastPage: 1,
      },
    };
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to fetch inquiry batches."),
    );
  }
}

/** GET /commission-inquiry-periods */
export async function fetchInquiryPeriods(params = {}) {
  try {
    const response = await apiUrl.get("/commission-inquiry-periods", {
      params: {
        application_type: params.applicationType || "education",
      },
    });
    const data = assertSuccess(response, "Failed to fetch inquiry periods.");
    return {
      periods: Array.isArray(data.data) ? data.data : [],
      openPeriod: data.open_period || null,
    };
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to fetch inquiry periods."),
    );
  }
}

/** POST /commission-inquiry-periods/open */
export async function openInquiryPeriod(params = {}) {
  try {
    const response = await apiUrl.post("/commission-inquiry-periods/open", {
      application_type: params.applicationType || "education",
      period: params.period || undefined,
    });
    const data = assertSuccess(response, "Failed to open inquiry period.");
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to open inquiry period."),
    );
  }
}

/** POST /commission-inquiry-periods/close */
export async function closeInquiryPeriod(params = {}) {
  try {
    const response = await apiUrl.post("/commission-inquiry-periods/close", {
      application_type: params.applicationType || "education",
      period: params.period || undefined,
    });
    const data = assertSuccess(response, "Failed to close inquiry period.");
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to close inquiry period."),
    );
  }
}

/** GET /commission-inquiry-batches/{id} */
export async function fetchInquiryBatch(batchId) {
  if (!batchId) throw new Error("Batch id is required.");
  try {
    const response = await apiUrl.get(`/commission-inquiry-batches/${batchId}`);
    const data = assertSuccess(response, "Failed to fetch inquiry batch.");
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to fetch inquiry batch."),
    );
  }
}

/** POST /commission-inquiry-batches/preview */
export async function previewInquiryBatchCreate(payload) {
  try {
    const response = await apiUrl.post(
      "/commission-inquiry-batches/preview",
      payload,
    );
    const data = assertSuccess(response, "Failed to preview inquiry batch.");
    return {
      eligible_count: data.eligible_count ?? 0,
      skipped_count: data.skipped_count ?? 0,
      eligible: Array.isArray(data.eligible) ? data.eligible : [],
      skipped: Array.isArray(data.skipped) ? data.skipped : [],
    };
  } catch (error) {
    const skipped = error?.response?.data?.skipped;
    const err = new Error(
      extractErrorMessage(error, "Failed to preview inquiry batch."),
    );
    if (Array.isArray(skipped)) {
      err.skipped = skipped;
    }
    throw err;
  }
}

/** POST /commission-inquiry-batches */
export async function createInquiryBatch(payload) {
  try {
    const response = await apiUrl.post("/commission-inquiry-batches", payload);
    const data = assertSuccess(response, "Failed to create inquiry batch.");
    return {
      batch: data.data,
      message: data.message,
      eligible_count: data.eligible_count,
      skipped_count: data.skipped_count ?? 0,
      skipped: Array.isArray(data.skipped) ? data.skipped : [],
    };
  } catch (error) {
    const payload = error?.response?.data;
    const err = new Error(
      extractErrorMessage(error, "Failed to create inquiry batch."),
    );
    if (Array.isArray(payload?.skipped)) {
      err.skipped = payload.skipped;
    }
    throw err;
  }
}

/** POST /commission-inquiry-batches/{id}/update */
export async function updateInquiryBatch(batchId, payload) {
  if (!batchId) throw new Error("Batch id is required.");
  try {
    const response = await apiUrl.post(
      `/commission-inquiry-batches/${batchId}/update`,
      payload,
    );
    const data = assertSuccess(response, "Failed to update inquiry batch.");
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to update inquiry batch."),
    );
  }
}

/** POST /commission-inquiry-batches/{id}/send */
export async function markInquiryBatchSent(batchId) {
  if (!batchId) throw new Error("Batch id is required.");
  try {
    const response = await apiUrl.post(
      `/commission-inquiry-batches/${batchId}/send`,
    );
    const data = assertSuccess(response, "Failed to mark batch as sent.");
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to mark batch as sent."),
    );
  }
}

/** POST /commission-inquiry-batches/{id}/items */
export async function addItemsToInquiryBatch(batchId, payload) {
  if (!batchId) throw new Error("Batch id is required.");
  try {
    const response = await apiUrl.post(
      `/commission-inquiry-batches/${batchId}/items`,
      payload,
    );
    const data = assertSuccess(response, "Failed to add items to batch.");
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to add items to batch."),
    );
  }
}

function normalizeBatchItemIds(payload = {}) {
  const raw = Array.isArray(payload.batch_item_ids)
    ? payload.batch_item_ids
    : payload.batch_item_id != null && payload.batch_item_id !== ""
      ? [payload.batch_item_id]
      : [];

  return [
    ...new Set(
      raw.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0),
    ),
  ];
}

/**
 * POST /commission-inquiry-batches/{id}/reply
 * Reply = notes only. Files go in commission_inquiry_reply_documents.
 * One request for all selected students (batch_item_ids + notes + documents).
 */
export async function saveInquiryBatchReply(batchId, payload = {}) {
  if (!batchId) throw new Error("Batch id is required.");

  const itemIds = normalizeBatchItemIds(payload);

  if (!itemIds.length) {
    throw new Error("Select at least one student to reply.");
  }

  const notes = String(
    payload.notes || payload.reply_notes || payload.details || "",
  ).trim();
  const files = []
    .concat(payload.documents || [])
    .concat(payload.document ? [payload.document] : [])
    .filter(Boolean);

  if (!notes && files.length === 0) {
    throw new Error("Enter reply notes and/or upload a document.");
  }

  try {
    let response;

    if (files.length > 0) {
      const formData = new FormData();
      // Indexed keys keep every id/file (bare [] often collapses to the last in PHP)
      formData.append("batch_item_ids_json", JSON.stringify(itemIds));
      itemIds.forEach((id, index) => {
        formData.append(`batch_item_ids[${index}]`, String(id));
      });
      if (notes) formData.append("notes", notes);
      files.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });

      response = await apiUrl.post(
        `/commission-inquiry-batches/${batchId}/reply`,
        formData,
        { headers: { "Content-Type": undefined } },
      );
    } else {
      response = await apiUrl.post(
        `/commission-inquiry-batches/${batchId}/reply`,
        {
          batch_item_ids: itemIds,
          notes,
        },
      );
    }

    const data = assertSuccess(response, "Failed to save provider reply.");
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to save provider reply."),
    );
  }
}

export async function updateInquiryBatchItem(batchId, itemId, payload) {
  if (!batchId) throw new Error("Batch id is required.");
  if (!itemId) throw new Error("Batch item id is required.");

  try {
    const response = await apiUrl.post(
      `/commission-inquiry-batches/${batchId}/items/${itemId}/update`,
      payload,
    );
    const data = assertSuccess(response, "Failed to update batch item.");
    return data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to update batch item."));
  }
}

/** GET /getTagsByType/{type} */
export async function fetchEmailTags(type = "email") {
  try {
    const response = await apiUrl.get(`/getTagsByType/${type}`);
    if (response?.data?.status && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return [];
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to fetch email tags."));
  }
}

/** POST /commission-inquiry-batches/{id}/provider-email/preview */
export async function previewProviderEmailSend(batchId, batchItemIds = []) {
  if (!batchId) throw new Error("Batch id is required.");

  const ids = [
    ...new Set(
      (Array.isArray(batchItemIds) ? batchItemIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    ),
  ];

  if (!ids.length) {
    throw new Error("Select at least one student.");
  }

  try {
    const response = await apiUrl.post(
      `/commission-inquiry-batches/${batchId}/provider-email/preview`,
      { batch_item_ids: ids },
    );
    const data = assertSuccess(
      response,
      "Failed to load provider email preview.",
    );
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to load provider email preview."),
    );
  }
}

/** POST /commission-inquiry-batches/{id}/provider-email/send */
export async function sendProviderEmail(batchId, payload = {}) {
  if (!batchId) throw new Error("Batch id is required.");

  const batchItemIds = [
    ...new Set(
      (Array.isArray(payload.batch_item_ids) ? payload.batch_item_ids : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    ),
  ];

  if (!batchItemIds.length) {
    throw new Error("Select at least one student.");
  }

  const emailBody = String(payload.email_body || "").trim();
  if (!emailBody || isEmptyEmailHtml(emailBody)) {
    throw new Error("Email body is required.");
  }

  const subject = String(payload.subject || "").trim();
  if (!subject) {
    throw new Error("Subject is required.");
  }

  const recipientEmails = Array.isArray(payload.recipient_emails)
    ? payload.recipient_emails
    : [];

  const ccEmails = Array.isArray(payload.cc_emails)
    ? payload.cc_emails
        .map((email) => String(email || "").trim())
        .filter(Boolean)
    : [];

  try {
    const response = await apiUrl.post(
      `/commission-inquiry-batches/${batchId}/provider-email/send`,
      {
        batch_item_ids: batchItemIds,
        subject,
        email_body: emailBody,
        cc_emails: ccEmails,
        recipient_emails: recipientEmails,
      },
    );
    const data = assertSuccess(response, "Failed to record provider email.");
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to record provider email."),
    );
  }
}

/** POST /commission-inquiry-batch-items/{itemId}/apply */
export async function applyInquiryBatchItem(itemId) {
  if (!itemId) throw new Error("Item id is required.");
  try {
    const response = await apiUrl.post(
      `/commission-inquiry-batch-items/${itemId}/apply`,
    );
    const data = assertSuccess(
      response,
      "Failed to apply reply to commission.",
    );
    return data.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to apply reply to commission."),
    );
  }
}

/** POST /commission-inquiry-batch-items/{itemId}/cancel */
export async function cancelInquiryBatchItem(itemId) {
  if (!itemId) throw new Error("Item id is required.");
  try {
    const response = await apiUrl.post(
      `/commission-inquiry-batch-items/${itemId}/cancel`,
    );
    const data = assertSuccess(response, "Failed to cancel batch item.");
    return data.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to cancel batch item."));
  }
}
